import {
  useV2CreateClientMutation,
  useV2UpdateClientMutation,
  useV2DeleteClientMutation,
  useV2MergeClientRecordsMutation,
} from "@generated";
import type { V2ClientResource, V2EmbeddedProjectVariable } from "@generated";
import type { ClientFormValues, ProjectVariableField } from "../types";

// Build the JSON:API `data` body for create/update. `account` is required by
// the generated V2ClientResource type but is inferred from auth server-side,
// so we construct the object and cast.
export const buildClientData = (
  values: ClientFormValues,
  fields: ProjectVariableField[],
  id?: string
): V2ClientResource => {
  const udf: V2EmbeddedProjectVariable[] = fields.map((f) => ({
    name: f.name,
    value: values.udfValues[f.name] ?? "",
  }));

  return {
    type: "clients",
    ...(id ? { id } : {}),
    attributes: {
      name: values.name,
      domain: values.domain || undefined,
      "msa-date": values.msaDate || undefined,
      "user-defined-fields": udf,
    },
    ...(values.rateTableId
      ? {
          relationships: {
            "rate-table": {
              data: { type: "rate-tables", id: values.rateTableId },
            },
          },
        }
      : {}),
  } as unknown as V2ClientResource;
};

export const useClientMutations = () => {
  const [create, createState] = useV2CreateClientMutation();
  const [update, updateState] = useV2UpdateClientMutation();
  const [destroy, destroyState] = useV2DeleteClientMutation();
  const [mergeRecords, mergeState] = useV2MergeClientRecordsMutation();

  return {
    create: (values: ClientFormValues, fields: ProjectVariableField[]) =>
      create({ body: { data: buildClientData(values, fields) } }).unwrap(),

    update: (
      id: string,
      values: ClientFormValues,
      fields: ProjectVariableField[]
    ) =>
      update({
        id: id,
        body: { data: buildClientData(values, fields, id) },
      }).unwrap(),

    // Archive (soft-delete: the API stamps deleted_at; restore re-activates).
    archive: (id: string) => destroy({ id: id }).unwrap(),

    restore: (id: string, name: string) =>
      update({
        id: id,
        body: {
          data: {
            type: "clients",
            id,
            attributes: { name, active: true },
          } as unknown as V2ClientResource,
        },
      }).unwrap(),

    // Merge a single source client into a target: the target (URL id) keeps the
    // source's projects and stays active; the source is archived. The API
    // requires data.id to equal the target id.
    merge: (sourceId: string, targetId: string) =>
      mergeRecords({
        id: targetId,
        body: {
          data: {
            id: Number(targetId),
            type: "clients",
            attributes: { "client-id": Number(sourceId) },
          },
        },
      }).unwrap(),

    states: { createState, updateState, destroyState, mergeState },
  };
};

export default useClientMutations;
